import {EmailSetting} from './email-setting';
import {SocialMediaUrl} from './socialMediaUrl';
import {DeliverySetting, DeliverySettingService} from './delivery-setting';
import {Store} from './store_';
import {SocialMedia} from './socialMedia';
import {AppLink} from './appLink';
import {ItemsStore} from './itemsStore';
import {GetSafeValue} from '../utils/get-safe-value';
import {StringFieldUtil} from '../utils/string-field-util';
import {StringUtils} from '../utils/string-utils';
import {SystemConfigUtils} from '../utils/system-config-utils';
import {BankPayment} from './bank-payment';
import {TermsCondition} from './terms-condition';
import {CurrencyExchange} from './currency-exchange';
import {VariationOrder} from '../inventory/model/item';

export class StoreSetting {
  private _storeOrderConfirmationMessage: Map<string, string>;
  private _pushStatusDeliveryMessage: Map<string, string>;
  private _pushStatusProcessedMessage: Map<string, string>;
  private _emailSetting: EmailSetting;
  private _deliverySettings: DeliverySetting[];
  private _deliverySettingsService: DeliverySettingService;
  private _socialMedia: SocialMedia;
  private _stores: Store[];
  private _sameDayDeliveryCutOffTime: string;
  private _storePhoneNumber: string;
  private _storeBannerUrl: string;
  private _appLinks: AppLink;
  private _items: ItemsStore;
  private _storeId: string;
  private _about: string;
  private _businessType: string;
  private _storeName: string;
  private _countryCity: Map<string, string[]>;
  private _bank: BankPayment;
  private _termsConditions: TermsCondition[];
  private _suggestionVariations: VariationOrder;
  private _commentOnReceipt: string;
  private _currencyExchanges: CurrencyExchange[];

  constructor() {
    this.emailSetting = new EmailSetting();
    this.deliverySettingsService = new DeliverySettingService();
    this.socialMedia = new SocialMedia();
    this.storeOrderConfirmationMessage = new Map();
    this.pushStatusDeliveryMessage = new Map();
    this.pushStatusProcessedMessage = new Map();
    this.stores = [];
    this.deliverySettings = [];
    this.appLinks = new AppLink();
    this.items = new ItemsStore();
    this.countryCity = new Map();
    this.bank = new BankPayment();
    this._termsConditions = [];
    this.suggestionVariations = new VariationOrder();
    this.currencyExchanges = [];
  }

  clone(data: StoreSetting) {
    this.sameDayDeliveryCutOffTime = data.sameDayDeliveryCutOffTime;
    this.storePhoneNumber = data.storePhoneNumber;
    this.storeOrderConfirmationMessage = data.storeOrderConfirmationMessage;
    this.pushStatusDeliveryMessage = data.pushStatusDeliveryMessage;
    this.pushStatusProcessedMessage = data.pushStatusProcessedMessage;
    this.storeId = data.storeId;
    this.about = data.about;
    this.commentOnReceipt = data.commentOnReceipt;
    this.businessType = data.businessType;
    this.emailSetting.clone(data.emailSetting);
    this.socialMedia.clone(data.socialMedia);
    this.appLinks.clone(data.appLinks);
    this.items.clone(data.items);
    this.storeName = data.storeName;
    this.storeBannerUrl = data.storeBannerUrl;
    this.countryCity = data.countryCity;
    this.suggestionVariations = data.suggestionVariations;

    data.currencyExchanges.forEach(value => {
      let obj = new CurrencyExchange();
      obj.clone(value);
      this.currencyExchanges.push(obj);
    });


    if (SystemConfigUtils.getIsSingleStoreBCSetting()) {
      data.deliverySettings.forEach((value) => {
        let delivery = new DeliverySetting();
        delivery.clone(value);
        this.deliverySettings.push(delivery);
      });
    } else {
      this.deliverySettingsService.clone(data.deliverySettingsService);
    }

    data.termsConditions.forEach((value => {
      let obj = new TermsCondition();
      obj.clone(value);
      this.termsConditions.push(obj);
    }));

    data.stores.forEach(store => {
      let obj = new Store();
      obj.clone(store);
      this.stores.push(obj);
    });

    this.bank.clone(data.bank);
  }

  formJson(data: StoreSetting) {
    this.storePhoneNumber = GetSafeValue.getSafeValueAsString(
      data[StringFieldUtil.storePhoneNumber] != null
        ? data[StringFieldUtil.storePhoneNumber].substr(4)
        : data[StringFieldUtil.storePhoneNumber], null
    );

    this.pushStatusDeliveryMessage = StringUtils.objToStrMap(GetSafeValue.getSafeValueAsMap(
      data[StringFieldUtil.pushStatusDeliveryMessage],
      {'en': '', 'kh': ''}
    ));

    this.pushStatusProcessedMessage = StringUtils.objToStrMap(GetSafeValue.getSafeValueAsMap(
      data[StringFieldUtil.pushStatusProcessedMessage],
      {'en': '', 'kh': ''}
    ));

    this.storeOrderConfirmationMessage = StringUtils.objToStrMap(GetSafeValue.getSafeValueAsMap(
      data[StringFieldUtil.storeOrderConfirmMessage],
      {'en': '', 'kh': ''}
    ));
    this.sameDayDeliveryCutOffTime = GetSafeValue.getSafeValueAsString(
      data[StringFieldUtil.sameDayDeliveryCutOffTime],
      null
    );
    this.storeId = GetSafeValue.getSafeValueAsString(
      data[StringFieldUtil.storeId],
      null
    );
    this.about = GetSafeValue.getSafeValueAsString(
      data[StringFieldUtil.about],
      null
    );

    this.commentOnReceipt = GetSafeValue.getSafeValueAsString(
      data[StringFieldUtil.commentOnReceipt],
      null
    );

    this.businessType = GetSafeValue.getSafeValueAsString(
      data[StringFieldUtil.businessType],
      null
    );
    if (data[StringFieldUtil.socialMedia]) {
      this.socialMedia.fromJson(data[StringFieldUtil.socialMedia]);
    }
    if (data[StringFieldUtil.emailSetting]) {
      this.emailSetting.fromJson(data[StringFieldUtil.emailSetting]);
    }
    if (data[StringFieldUtil.appLinks]) {
      this.appLinks.fromJson(data[StringFieldUtil.appLinks]);
    }
    this.storeName = GetSafeValue.getSafeValueAsString(
      data[StringFieldUtil.storeName],
      null
    );

    this.storeBannerUrl = GetSafeValue.getSafeValueAsString(
      data[StringFieldUtil.storeBannerUrl],
      null
    );

    if (data[StringFieldUtil.currencyExchanges]) {
      data[StringFieldUtil.currencyExchanges].forEach(value => {
        let obj = new CurrencyExchange();
        obj.fromJson(value);
        this.currencyExchanges.push(obj);
      });
    }

    if (data[StringFieldUtil.countryCity]) {
      this.countryCity = StringUtils.objToStrMap(data[StringFieldUtil.countryCity]);
    }

    if (data[StringFieldUtil.items]) {
      this.items.fromJson(data[StringFieldUtil.items]);
    }

    if (data[StringFieldUtil.suggestionVariations]) {
      let variationOrder = new VariationOrder();
      variationOrder.fromJson(data[StringFieldUtil.suggestionVariations]);
      this.suggestionVariations = variationOrder;
    }

    if (SystemConfigUtils.getIsSingleStoreBCSetting() && data[StringFieldUtil.items]) {

      if (data[StringFieldUtil.deliverySettings] && data[StringFieldUtil.deliverySettings].length !== undefined) {
        data[StringFieldUtil.deliverySettings].forEach(value => {
          let delivery = new DeliverySetting();
          delivery.fromJson(value);
          this.deliverySettings.push(delivery);
        });
      }
    }

    if (!SystemConfigUtils.getIsSingleStoreBCSetting() && data[StringFieldUtil.deliverySettings]) {
      if (data[StringFieldUtil.deliverySettings]) {
        let delivery = new DeliverySettingService();
        delivery.fromJson(data[StringFieldUtil.deliverySettings]);
        this.deliverySettingsService = delivery;
      }
    }

    if (data[StringFieldUtil.stores]) {
      data[StringFieldUtil.stores].forEach(value => {
        let store = new Store();
        store.fromJson(value);
        this.stores.push(store);
      });
    }

    this.bank = new BankPayment();
    if (data[StringFieldUtil.bank]) {
      this.bank.fromJson(data[StringFieldUtil.bank]);
    }

    if (data[StringFieldUtil.termsAndCondition]) {
      data[StringFieldUtil.termsAndCondition].forEach(value => {
        let termsCondition = new TermsCondition();
        termsCondition.fromJson(value);
        this.termsConditions.push(termsCondition);
      });
    }
  }

  get commentOnReceipt(): string {
    return this._commentOnReceipt;
  }

  set commentOnReceipt(value: string) {
    this._commentOnReceipt = value;
  }

  get pushStatusProcessedMessage(): Map<string, string> {
    return this._pushStatusProcessedMessage;
  }

  set pushStatusProcessedMessage(value: Map<string, string>) {
    this._pushStatusProcessedMessage = value;
  }

  get storeBannerUrl(): string {
    return this._storeBannerUrl;
  }

  set storeBannerUrl(value: string) {
    this._storeBannerUrl = value;
  }

  get about(): string {
    return this._about;
  }

  set about(value: string) {
    this._about = value;
  }

  get businessType(): string {
    return this._businessType;
  }

  set businessType(value: string) {
    this._businessType = value;
  }

  get storeId(): string {
    return this._storeId;
  }

  set storeId(value: string) {
    this._storeId = value;
  }

  get storeOrderConfirmationMessage(): Map<string, string> {
    return this._storeOrderConfirmationMessage;
  }

  set storeOrderConfirmationMessage(value: Map<string, string>) {
    this._storeOrderConfirmationMessage = value;
  }

  get items(): ItemsStore {
    return this._items;
  }

  set items(value: ItemsStore) {
    this._items = value;
  }

  get storePhoneNumber() {
    return this._storePhoneNumber;
  }


  set storePhoneNumber(value) {
    this._storePhoneNumber = value;
  }

  get emailSetting(): EmailSetting {
    return this._emailSetting;
  }

  set emailSetting(value: EmailSetting) {
    this._emailSetting = value;
  }


  get deliverySettings(): DeliverySetting[] {
    return this._deliverySettings;
  }

  set deliverySettings(value: DeliverySetting[]) {
    this._deliverySettings = value;
  }

  get deliverySettingsService(): DeliverySettingService {
    return this._deliverySettingsService;
  }

  set deliverySettingsService(value: DeliverySettingService) {
    this._deliverySettingsService = value;
  }

  get socialMedia(): SocialMedia {
    return this._socialMedia;
  }

  set socialMedia(value: SocialMedia) {
    this._socialMedia = value;
  }

  get stores(): Store[] {
    return this._stores;
  }

  set stores(value: Store[]) {
    this._stores = value;
  }

  get sameDayDeliveryCutOffTime() {
    return this._sameDayDeliveryCutOffTime;
  }

  set sameDayDeliveryCutOffTime(value) {
    this._sameDayDeliveryCutOffTime = value;
  }


  get pushStatusDeliveryMessage(): Map<string, string> {
    return this._pushStatusDeliveryMessage;
  }

  set pushStatusDeliveryMessage(value: Map<string, string>) {
    this._pushStatusDeliveryMessage = value;
  }

  get storeName(): string {
    return this._storeName;
  }

  set storeName(value: string) {
    this._storeName = value;
  }

  get appLinks(): AppLink {
    return this._appLinks;
  }

  set appLinks(value: AppLink) {
    this._appLinks = value;
  }

  get countryCity(): Map<string, string[]> {
    return this._countryCity;
  }

  set countryCity(value: Map<string, string[]>) {
    this._countryCity = value;
  }

  get bank(): BankPayment {
    return this._bank;
  }

  set bank(value: BankPayment) {
    this._bank = value;
  }

  get termsConditions(): TermsCondition[] {
    return this._termsConditions;
  }

  set termsConditions(value: TermsCondition[]) {
    this._termsConditions = value;
  }

  get suggestionVariations(): VariationOrder {
    return this._suggestionVariations;
  }

  set suggestionVariations(value: VariationOrder) {
    this._suggestionVariations = value;
  }

  get currencyExchanges(): CurrencyExchange[] {
    return this._currencyExchanges;
  }

  set currencyExchanges(value: CurrencyExchange[]) {
    this._currencyExchanges = value;
  }

  toMap(isWhiteLabelPlatform, isDeliverySave: boolean = false) {
    const storeToMap = [];
    this.stores.forEach((value => {
        storeToMap.push(value.toMap());
      }
    ));

    let deliverySettings: any = undefined;

    if (SystemConfigUtils.getIsSingleStoreBCSetting()) {
      deliverySettings = [];
      this.deliverySettings.forEach((value => {
          deliverySettings.push(value.toMap());
        }
      ));
    } else {
      deliverySettings = this.deliverySettingsService.toMapByStore();
    }

    let face = new SocialMediaUrl();
    face.clone(this.socialMedia.facebook);
    let inst = new SocialMediaUrl();
    inst.clone(this.socialMedia.instagram);
    let web = new SocialMediaUrl();
    web.clone(this.socialMedia.website);

    this.socialMedia.facebook = face;
    this.socialMedia.instagram = inst;
    this.socialMedia.website = web;

    const termsConditionsList = [];
    this.termsConditions.forEach((value => {
        termsConditionsList.push(value.toMap());
      }
    ));

    const currencyExchangesList = [];
    this.currencyExchanges.forEach(value => {
      currencyExchangesList.push(value.toMap());
    });

    if (SystemConfigUtils.getIsSingleStoreBCSetting()) {
      return {
        'storeOrderConfirmMessage': StringUtils.strMapToObj(this.storeOrderConfirmationMessage) ? StringUtils.strMapToObj(this.storeOrderConfirmationMessage) : null,
        'pushStatusDeliveryMessage': StringUtils.strMapToObj(this.pushStatusDeliveryMessage) ? StringUtils.strMapToObj(this.pushStatusDeliveryMessage) : null,
        'pushStatusProcessedMessage': StringUtils.strMapToObj(this.pushStatusProcessedMessage) ? StringUtils.strMapToObj(this.pushStatusProcessedMessage) : null,
        'emailSetting': this.emailSetting.toMap() ? this.emailSetting.toMap() : null,
        'deliverySettings': deliverySettings ? deliverySettings : null,
        'socialMedia': this.socialMedia.toMap() ? this.socialMedia.toMap() : null,
        'stores': storeToMap ? storeToMap : null,
        'sameDayDeliveryCutOffTime': this.sameDayDeliveryCutOffTime ? this.sameDayDeliveryCutOffTime : null,
        'storePhoneNumber': '+855' + (this.storePhoneNumber ? this.storePhoneNumber : ''),
        'appLinks': this.appLinks.toMap() ? this.appLinks.toMap() : null,
        'items': this.items.toMap() ? this.items.toMap() : null,
        'storeId': this.storeId ? this.storeId : null,
        'businessType': !this.businessType ? null : this.businessType,
        'about': !this.about ? null : this.about,
        'storeName': this.storeName ? this.storeName : null,
        'bank': this.bank.toMap() ? this.bank.toMap() : null,
        'terms and condition': termsConditionsList ? termsConditionsList : null,
        'currencyExchanges': currencyExchangesList
      };
    }

    let map = Object();
    map = {
      'emailSetting': this.emailSetting.toMap() ? this.emailSetting.toMap() : null,
      'stores': storeToMap,
      'sameDayDeliveryCutOffTime': this.sameDayDeliveryCutOffTime ? this.sameDayDeliveryCutOffTime : null,
      'storePhoneNumber': '+855' + (this.storePhoneNumber ? this.storePhoneNumber : ''),
      'items': this.items.toMap() ? this.items.toMap() : null,
      'storeId': this.storeId ? this.storeId : null,
      'businessType': !this.businessType ? null : this.businessType,
      'about': !this.about ? null : this.about,
      "commentOnReceipt": !this.commentOnReceipt ? null : this.commentOnReceipt,
      'storeName': this.storeName ? this.storeName : null,
      'bank': this.bank.toMap() ? this.bank.toMap() : null,
      'storeBannerUrl': this.storeBannerUrl,
      'terms and condition': termsConditionsList,
      'currencyExchanges': currencyExchangesList
    };

    if (isDeliverySave) {
      map = {};
      map.deliverySettings = deliverySettings ? deliverySettings : null;
    }

    if (isWhiteLabelPlatform === true) {
      map.storeOrderConfirmMessage = StringUtils.strMapToObj(this.storeOrderConfirmationMessage) ? StringUtils.strMapToObj(this.storeOrderConfirmationMessage) : null;
      map.pushStatusDeliveryMessage = StringUtils.strMapToObj(this.pushStatusDeliveryMessage) ? StringUtils.strMapToObj(this.pushStatusDeliveryMessage) : null;
      map.pushStatusProcessedMessage = StringUtils.strMapToObj(this.pushStatusProcessedMessage) ? StringUtils.strMapToObj(this.pushStatusProcessedMessage) : null;
    }

    return map;

  }

  public toMapForDefault(isVersitaAdmin?: boolean) {
    const currencyExchangesList = [];
    this.currencyExchanges.forEach(value => {
      currencyExchangesList.push(value.toMap());
    });

    if (isVersitaAdmin) {
      return {
        'storeOrderConfirmMessage': StringUtils.strMapToObj(this.storeOrderConfirmationMessage),
        'pushStatusDeliveryMessage': StringUtils.strMapToObj(this.pushStatusDeliveryMessage),
        'pushStatusProcessedMessage': StringUtils.strMapToObj(this.pushStatusProcessedMessage),
        'emailSetting': this.emailSetting.toMapForDefault(),
        'appLinks': this.appLinks === null ? null : this.appLinks.toMap(),
        'countryCity': StringUtils.strMapToObj(this.countryCity),
        'storeBannerUrl': this.storeBannerUrl,
        'currencyExchanges': currencyExchangesList
      };
    }

    return {
      'storeOrderConfirmMessage': StringUtils.strMapToObj(this.storeOrderConfirmationMessage),
      'pushStatusDeliveryMessage': StringUtils.strMapToObj(this.pushStatusDeliveryMessage),
      'pushStatusProcessedMessage': StringUtils.strMapToObj(this.pushStatusProcessedMessage),
      'emailSetting': this.emailSetting.toMapForDefault(),
      'appLinks': this.appLinks === null ? null : this.appLinks.toMap(),
      'currencyExchanges': currencyExchangesList
    };
  }

  public static buildSampleData(): StoreSetting {
    let emailSetting = new EmailSetting();
    emailSetting.adminEmail = 'versita.store.admin@gmail.com';
    emailSetting.adminPassword = 'store.123';
    emailSetting.deliveryEmailTeam = ['versita.store.dev@gmail.com'];
    emailSetting.packageEmailTeam = ['versita.store.dev@gmail.com'];
    emailSetting.recipientEmailTeam = ['versita.store.dev@gmail.com'];

    let storeSetting = new StoreSetting();
    let facebook = new SocialMediaUrl();
    let instagram = new SocialMediaUrl();
    let website = new SocialMediaUrl();

    facebook.webUrl = 'https://www.facebook.com/Amazon/';
    facebook.iosUrl = 'fb://profile/9465008123';
    facebook.androidUrl = 'https://www.facebook.com/Amazon/';

    instagram.webUrl = 'https://www.instagram.com/amazon/?hl=en';
    instagram.iosUrl = 'instagram://@amazon';
    instagram.androidUrl = 'instagram://@amazon';

    website.webUrl = 'https://www.amazon.fr/';
    website.iosUrl = 'https://www.amazon.fr/';
    website.androidUrl = 'https://www.amazon.fr/';

    storeSetting.emailSetting = emailSetting;
    storeSetting.socialMedia.facebook = facebook;
    storeSetting.socialMedia.instagram = instagram;
    storeSetting.socialMedia.website = website;
    storeSetting.sameDayDeliveryCutOffTime = '2019-01-19 08:00:00.000Z';
    storeSetting.pushStatusDeliveryMessage = new Map();
    storeSetting.pushStatusDeliveryMessage['en'] = 'kkkkk';
    storeSetting.pushStatusDeliveryMessage['kh'] = 'kkkkk';
    storeSetting.storeOrderConfirmationMessage['en'] = 'kkkk';
    storeSetting.storeOrderConfirmationMessage['kh'] = 'kkkk';
    storeSetting.pushStatusDeliveryMessage = new Map();
    storeSetting.pushStatusProcessedMessage['en'] = '';
    storeSetting.pushStatusProcessedMessage['kh'] = '';
    let store1 = new Store();
    let storeMapUrl = new SocialMediaUrl();
    store1.name['en'] = 'kk';
    store1.name['kh'] = 'kk';
    store1.address['en'] = 'kk';
    store1.address['kh'] = 'kk';
    store1.openHour['kh'] = 'kk';
    store1.openHour['en'] = 'kk';
    store1.phoneNumber = '987654321';
    storeMapUrl.iosUrl = 'https://www.instagram.com/amazon/?hl=en';
    storeMapUrl.androidUrl = 'https://www.instagram.com/amazon/?hl=en';
    storeMapUrl.webUrl = 'https://www.instagram.com/amazon/?hl=en';
    store1.storeMapUrl = storeMapUrl;

    let store2 = new Store();
    let storeMapUrl2 = new SocialMediaUrl();
    store2.name['en'] = 'kk2';
    store2.name['kh'] = 'kk2';
    store2.address['en'] = 'kk2';
    store2.address['kh'] = 'kk2';
    store2.openHour['kh'] = 'kk2';
    store2.openHour['en'] = 'kk2';
    store2.phoneNumber = '987654321';
    storeMapUrl2.iosUrl = 'https://www.instagram.com/amazon/?hl=en';
    storeMapUrl2.androidUrl = 'https://www.instagram.com/amazon/?hl=en';
    storeMapUrl2.webUrl = 'https://www.instagram.com/amazon/?hl=en';
    store2.storeMapUrl = storeMapUrl2;

    storeSetting.stores.push(store1);
    storeSetting.stores.push(store2);

    let currencyExchange = new CurrencyExchange();
    currencyExchange.from = "USD";
    currencyExchange.pair = "USD/KHR";
    currencyExchange.rate = 4000;
    currencyExchange.symbol = "៛";
    currencyExchange.to = "KHR";
    storeSetting.currencyExchanges.push(currencyExchange);

    return storeSetting;
  }

  public getCitiesOfCountry(country: string): string[] {
    return this.countryCity.get(country);
  }

}
